import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminRoute } from "@/components/auth/AdminRoute";

// Keep Index eagerly loaded for fastest LCP
import Index from "./pages/Index";

// Lazy load all other pages for code splitting
const Auth = lazy(() => import("./pages/Auth"));
const Contact = lazy(() => import("./pages/Contact"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Missions = lazy(() => import("./pages/Missions"));
const Packs = lazy(() => import("./pages/Packs"));
const Wallet = lazy(() => import("./pages/Wallet"));
const Affiliate = lazy(() => import("./pages/Affiliate"));
const Points = lazy(() => import("./pages/Points"));
const Profile = lazy(() => import("./pages/Profile"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const Challenges = lazy(() => import("./pages/Challenges"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Legal = lazy(() => import("./pages/Legal"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Communaute = lazy(() => import("./pages/Communaute"));
const CommunauteMerci = lazy(() => import("./pages/CommunauteMerci"));

// Admin pages - lazy loaded
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminStatistics = lazy(() => import("./pages/admin/AdminStatistics"));
const AdminFinances = lazy(() => import("./pages/admin/AdminFinances"));
const AdminDeposits = lazy(() => import("./pages/admin/AdminDeposits"));
const AdminWithdrawals = lazy(() => import("./pages/admin/AdminWithdrawals"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminMissions = lazy(() => import("./pages/admin/AdminMissions"));
const AdminAffiliation = lazy(() => import("./pages/admin/AdminAffiliation"));
const AdminPacks = lazy(() => import("./pages/admin/AdminPacks"));
const AdminPaymentMethods = lazy(() => import("./pages/admin/AdminPaymentMethods"));
const AdminAnnouncements = lazy(() => import("./pages/admin/AdminAnnouncements"));
const AdminActivityLogs = lazy(() => import("./pages/admin/AdminActivityLogs"));
const AdminFraudAlerts = lazy(() => import("./pages/admin/AdminFraudAlerts"));
const AdminTeam = lazy(() => import("./pages/admin/AdminTeam"));

const queryClient = new QueryClient();

// Minimal loading fallback to avoid layout shift
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public */}
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/legal" element={<Legal />} />
              <Route path="/communaute" element={<Communaute />} />
              <Route path="/communaute/merci" element={<CommunauteMerci />} />
              
              {/* Onboarding - Protected */}
              <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
              
              {/* User Dashboard - Protected */}
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/missions" element={<ProtectedRoute><Missions /></ProtectedRoute>} />
              <Route path="/packs" element={<ProtectedRoute><Packs /></ProtectedRoute>} />
              <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
              <Route path="/affiliate" element={<ProtectedRoute><Affiliate /></ProtectedRoute>} />
              <Route path="/points" element={<ProtectedRoute><Points /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
              <Route path="/challenges" element={<ProtectedRoute><Challenges /></ProtectedRoute>} />
              
              {/* Admin Dashboard - Admin Role Required */}
              <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              <Route path="/admin/statistics" element={<AdminRoute><AdminStatistics /></AdminRoute>} />
              <Route path="/admin/finances" element={<AdminRoute><AdminFinances /></AdminRoute>} />
              <Route path="/admin/deposits" element={<AdminRoute><AdminDeposits /></AdminRoute>} />
              <Route path="/admin/withdrawals" element={<AdminRoute><AdminWithdrawals /></AdminRoute>} />
              <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
              <Route path="/admin/missions" element={<AdminRoute><AdminMissions /></AdminRoute>} />
              <Route path="/admin/affiliation" element={<AdminRoute><AdminAffiliation /></AdminRoute>} />
              <Route path="/admin/packs" element={<AdminRoute><AdminPacks /></AdminRoute>} />
              <Route path="/admin/payment-methods" element={<AdminRoute><AdminPaymentMethods /></AdminRoute>} />
              <Route path="/admin/announcements" element={<AdminRoute><AdminAnnouncements /></AdminRoute>} />
              <Route path="/admin/logs" element={<AdminRoute><AdminActivityLogs /></AdminRoute>} />
              <Route path="/admin/fraud-alerts" element={<AdminRoute><AdminFraudAlerts /></AdminRoute>} />
              <Route path="/admin/team" element={<AdminRoute><AdminTeam /></AdminRoute>} />
              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
